<template>
  <div class="relative">
    <button v-if="showCloseButton" class="absolute top-0 right-0" @click="close">
      <CloseIcon color="#4B4C52" class="w-24 h-24" />
    </button>
    <form @submit.prevent="submit">
      <template v-if="tips.length === 1">
        <p class="text-body text-gray-body mb-16">{{ tips[0] }}</p>
      </template>
      <template v-else>
        <h3 class="font-medium text-m1 mr-24">{{ title }}</h3>
        <CheckmarkList :tips="tips" />
      </template>
      <slot />
      <TextArea
        id="start-the-chat"
        v-model="messageBoxText"
        class="mt-16"
        :rows="4"
        prop-classes="!resize-none"
        :max-characters="maxCharacters"
        :presence-required="true"
        :character-counter="false"
        :label-name="labelName"
        :status="helperStatus"
        :helper-text="helperText"
      />
      <Button value="Send" class="w-full mt-16" size="medium" type="submit" :loading="loading" />
    </form>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { HelperStatus } from '@/helpers/status';
import Button from '@/components/button/Button.vue';
import TextArea from '@/components/textarea/TextArea.vue';
import CheckmarkList from '@/components/checkmark_list/CheckmarkList.vue';
import CloseIcon from '@/graphics/icons/CloseIcon.vue';

export default {
  name: 'SendMessageInput',
  components: { TextArea, Button, CheckmarkList, CloseIcon },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: true,
    },
    defaultMessage: {
      type: String,
      default: '',
    },
    tips: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    labelName: {
      type: String,
      required: true,
    },
    validationMessage: {
      type: String,
      default: 'Please include a message.',
    },
    modelValue: {
      type: String,
      default: '',
    },
    maxCharacters: {
      type: Number,
      default: 4000,
    },
  },
  emits: ['submit', 'close', 'closeDraft', 'update:modelValue'],
  data() {
    return {
      helperStatus: HelperStatus.Neutral,
      messageBoxText: this.defaultMessage,
    };
  },
  computed: {
    helperText(): string {
      if (this.isMessageTooLong) {
        return '';
      }
      if (this.helperStatus === HelperStatus.Error) {
        return this.validationMessage;
      }
      return '';
    },
    trimmedMessage(): string {
      return this.messageBoxText.trim();
    },
    isValidMessage(): boolean {
      return this.trimmedMessage.length > 0 && !this.isMessageTooLong;
    },
    isMessageTooLong(): boolean {
      return this.trimmedMessage.length > this.maxCharacters;
    },
  },
  watch: {
    // setup for parent v-model
    modelValue(value: string) {
      this.messageBoxText = value;
    },
    messageBoxText(value: string): void {
      if (this.isValidMessage) {
        this.helperStatus = HelperStatus.Valid;
      }
      this.$emit('update:modelValue', value);
    },
  },
  methods: {
    submit(): void {
      if (this.isValidMessage) {
        this.$emit('submit', this.trimmedMessage);
      } else {
        this.helperStatus = HelperStatus.Error;
      }
    },
    close(): void {
      if (this.isValidMessage) {
        this.$emit('closeDraft');
      } else {
        this.$emit('close');
      }
    },
  },
};
</script>
